import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/CloseTwoTone';
import currencyFormatter from '@sprinx/formatting/currencyFormatter';
import ProductImage from '@sprinx/react-com/ProductImage';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { currencyState, localeState, showPricesState } from '../../api/appState';
import { productBuildHref } from '../../api/products/transformProduct';
import { ShoppingCartProduct, ShoppingCartSubject, useAddToCart, useRemoveFromCart } from '../../api/shoppingCart';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';
import QuantityField from '../QuantityField';

export interface ShoppingCartAsideContentRowProps extends StandardDivProps<ShoppingCartAsideContentRowClassKey> {
  subject: ShoppingCartSubject;
}

export type ShoppingCartAsideContentRowClassKey = 'root' | 'name' | 'actions' | 'image' | 'quantity' | 'price';

const themeSettings = { name: 'ShoppingCartAsideContentRow' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ShoppingCartAsideContentRowClassKey, {}>({
      root: {
        display: 'grid',
        gridColumnGap: 0,
        gridRowGap: 0,
        justifyItems: 'stretch',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        gridTemplateColumns: '68px auto auto 24px',
        gridTemplateRows: 'auto',
        gridTemplateAreas: `
        "name       name       name       actions"
        "image      quantity   price      price"
      `,
        padding: theme.spacing(1, 0.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      name: {
        ...theme.typography.subtitle1,
        color: theme.palette.primary.main,
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: '12px !important',

        gridArea: 'name',
      },
      actions: {
        gridArea: 'actions',
        overflow: 'hidden',

        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      },
      image: {
        gridArea: 'image',

        width: theme.spacing(8),
        height: theme.spacing(6),
      },
      quantity: {
        gridArea: 'quantity',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
      price: {
        gridArea: 'price',

        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
      },
    }),
  themeSettings,
);

const ShoppingCartAsideContentRow = React.forwardRef<HTMLDivElement, ShoppingCartAsideContentRowProps>(
  ({ className, classes: pClasses, subject }, ref) => {
    const t = useTranslateWithFallback();
    const classes = useStyles({ classes: pClasses });
    const currency = useRecoilValue(currencyState);
    const showPrices = useRecoilValue(showPricesState);
    const locale = useRecoilValue(localeState);

    const product = typeof subject.product === 'string' ? ({} as ShoppingCartProduct) : subject.product;

    const { addToCart, ...addToCartProps } = useAddToCart({
      cartSubject: subject,
    });
    const removeFromCart = useRemoveFromCart({
      cartSubject: subject,
    });

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <div className={classes.name}>
          <Link component={RouterLink} to={productBuildHref(locale, product)} variant='inherit' color='inherit'>
            {t(product.name, { acceptString: true })}
          </Link>
        </div>
        <div className={classes.actions}>
          <IconButton size='small' onClick={() => removeFromCart()}>
            <CloseIcon color='inherit' fontSize='inherit' />
          </IconButton>
        </div>
        <div className={classes.image}>
          <ProductImage alt={t(product.name, { acceptString: true })} src={product.image} size='small' />
        </div>
        <div className={classes.quantity}>
          <QuantityField onChange={addToCart} {...addToCartProps} fullWidth />
        </div>
        {showPrices && (
          <div className={classes.price}>
            <div style={{ fontWeight: 'bold' }}>{currencyFormatter(subject.subjectTotal, currency)}</div>
            <div>{currencyFormatter(subject.subjectTotalWithTax, currency)}</div>
          </div>
        )}
      </div>
    );
  },
);

ShoppingCartAsideContentRow.displayName = 'ShoppingCartAsideContentRow';

export default withThemeProps(themeSettings)(ShoppingCartAsideContentRow);
